import {
    Award,
    BookOpenText,
    CheckCircle2,
    Clock,
    Crosshair,
    Download,
    DollarSign,
    FileText,
    GraduationCap,
    Grid2x2,
    Info,
    MessageCircleWarning,
    NotebookPen,
    PackageSearch,
    Pen,
    Phone,
    RefreshCcw,
    Search,
    Table2,
    Target,
    Timer,
    Trash,
} from 'lucide-react';
import { LegendItem } from '../components/drawer/ButtonLegendContent';

export type ButtonLegendPage =
    | 'activity'
    | 'memberCompetency'
    | 'memberEndorsement'
    | 'dedicatedStatus'
    | 'score'
    | 'memberFirearm'
    | 'competitionEntry';

interface ButtonLegendOptions {
    // Activity only: the compliance "Full History" button is only visible for dedicated status members.
    showFullHistory?: boolean;
}

const viewToggleLegendItems: LegendItem[] = [
    { icon: <Grid2x2 />, color: 'orange', label: 'Card View', description: 'Switch to card view.' },
    { icon: <Table2 />, color: 'orange', label: 'List View', description: 'Switch to table/list view.' },
];

const fullHistoryLegendItem: LegendItem = {
    icon: <Clock />, color: 'blue', label: 'Full History', description: 'View your full multi-year compliance history.',
};

// Pages whose data cards sit behind a Card View / List View toggle. Dedicated Status has no toggle.
const pagesWithViewToggle: ButtonLegendPage[] = [
    'activity',
    'memberCompetency',
    'memberEndorsement',
    'score',
    'memberFirearm',
];

const domainLegendItems: Record<ButtonLegendPage, LegendItem[]> = {
    activity: [
        { icon: <MessageCircleWarning />, color: 'blue', label: 'Feedback', description: 'View feedback provided on this activity.' },
        { icon: <NotebookPen />, color: 'blue', label: 'My Notes', description: 'View your personal notes for this activity.' },
        { icon: <Pen />, color: 'orange', label: 'Edit', description: 'Edit this activity.' },
        { icon: <Trash />, color: 'red', label: 'Delete', description: 'Delete this activity.' },
    ],
    memberCompetency: [
        { icon: <Phone />, color: 'blue', label: 'Contact Admin', description: 'Contact the admin office about an expired competency.' },
        { icon: <RefreshCcw />, color: 'green', label: 'Renew', description: 'Start the renewal process for an active or expiring competency.' },
        { icon: <Pen />, color: 'orange', label: 'Edit', description: 'Edit the details of this competency.' },
        { icon: <Trash />, color: 'red', label: 'Delete', description: 'Delete this competency record.' },
    ],
    memberEndorsement: [
        { icon: <MessageCircleWarning />, color: 'blue', label: 'Feedback', description: 'View feedback provided on this endorsement.' },
        { icon: <Search />, color: 'blue', label: 'Track Firearm License', description: 'Track the SAPS firearm licence status for an approved endorsement.' },
        { icon: <FileText />, color: 'blue', label: 'Declaration', description: 'Generate the firearm licence declaration document for a renewal.' },
        { icon: <Pen />, color: 'orange', label: 'Edit', description: 'Edit this endorsement.' },
        { icon: <Trash />, color: 'red', label: 'Delete', description: 'Delete this endorsement.' },
        { icon: <Download />, color: 'green', label: 'Download Certificate', description: 'Download the certificate for an approved endorsement.' },
    ],
    dedicatedStatus: [
        { icon: <Phone />, color: 'blue', label: 'Contact Admin', description: 'Contact the admin office about this application.' },
        { icon: <MessageCircleWarning />, color: 'blue', label: 'Feedback', description: 'View feedback provided on this application.' },
        { icon: <PackageSearch />, color: 'blue', label: 'Track Your Package', description: 'Track the courier delivery of your printed course material.' },
        { icon: <DollarSign />, color: 'green', label: 'Make a Payment', description: 'Pay for this application.' },
        { icon: <Pen />, color: 'orange', label: 'Edit', description: 'Edit this application.' },
        { icon: <Trash />, color: 'red', label: 'Delete', description: 'Delete this application.' },
        { icon: <Award />, color: 'green', label: 'Download Certificate', description: 'Download your dedicated status certificate.' },
        { icon: <BookOpenText />, color: 'orange', label: 'Download Study Material', description: 'Download the course study material.' },
        { icon: <Target />, color: 'orange', label: 'Download Practical Target', description: 'Download the practical target for this course.' },
        { icon: <Crosshair />, color: 'green', label: 'Submit Practical Score', description: 'Submit your practical target score. You may resubmit if declined or incorrect.' },
        { icon: <GraduationCap />, color: 'green', label: 'Start Evaluation', description: 'Start the course evaluation.' },
        {
            icon: (
                <span className="relative inline-flex items-center justify-center">
                    <Award />
                    <span className="pointer-events-none absolute left-1/2 top-1/2 h-[2px] w-[135%] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-current" />
                </span>
            ),
            color: 'red',
            label: 'Forfeit Dedicated Status',
            description: 'Forfeit this dedicated status.',
        },
        { icon: <FileText />, color: 'blue', label: 'Download Forfeiture Document', description: 'Download the forfeiture document for this dedicated status.' },

    ],
    score: [
        { icon: <MessageCircleWarning />, color: 'blue', label: 'Feedback', description: 'View feedback provided on this score.' },
        { icon: <NotebookPen />, color: 'blue', label: 'Notes', description: 'View notes attached to this score.' },
        { icon: <Award />, color: 'green', label: 'Score Sections', description: 'View the number of sections submitted for this score.' },
        { icon: <Pen />, color: 'orange', label: 'Edit', description: 'Edit this score.' },
        { icon: <Trash />, color: 'red', label: 'Delete', description: 'Delete this score.' },
    ],
    memberFirearm: [
        { icon: <Phone />, color: 'blue', label: 'Contact Admin', description: 'Contact the admin office about an expired firearm licence.' },
        { icon: <Timer />, color: 'blue', label: 'Long Outstanding Application', description: 'Shown once your application reference number has been received and 90 work days have passed. Opens a form to submit your outstanding application to Natshoot, so they can help expedite the process with SAPS.' },
        { icon: <RefreshCcw />, color: 'green', label: 'Renew', description: 'Starts the renewal process for an active or expiring firearm licence by taking you to a new endorsement application with this firearm already selected.' },
        { icon: <Pen />, color: 'orange', label: 'Edit', description: 'Edit this firearm.' },
        { icon: <Trash />, color: 'red', label: 'Delete', description: 'Delete this firearm.' },
    ],
    competitionEntry: [
        { icon: <Info />, color: 'blue', label: 'View Competition Rules', description: 'View the rules for this competition.' },
        { icon: <Target />, color: 'orange', label: 'Download Competition Targets', description: 'Download the postal targets for this competition entry, once target downloads are open.' },
        { icon: <Crosshair />, color: 'green', label: 'Submit Score', description: 'Submit your competition score for this entry.' },
        { icon: <CheckCircle2 />, color: 'green', label: 'Score Submitted', description: 'A score has already been submitted for this entry.' },
        { icon: <DollarSign />, color: 'green', label: 'Make a Payment', description: 'Complete payment for this competition entry.' },
    ],
};

export function getButtonLegendItems(page: ButtonLegendPage, options: ButtonLegendOptions = {}): LegendItem[] {
    const items = [...domainLegendItems[page]];

    if (page === 'activity' && options.showFullHistory) {
        items.push(fullHistoryLegendItem);
    }

    if (pagesWithViewToggle.includes(page)) {
        items.push(...viewToggleLegendItems);
    }

    return items;
}
